import { Avatar, Button } from '@commenda/kakashi';
import React from 'react';
import { useNotification } from '../hooks/useNotification';
import { AvatarCropDialog } from './AvatarCropDialog';
import {
  ALLOWED_AVATAR_TYPES,
  MAX_AVATAR_SIZE_BYTES,
  readFileAsDataUrl,
} from './helpers';

type Props = {
  /** Current avatar as raw base64 (no `data:` prefix), or null when unset. */
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  /** Used for the rendered img's alt text and the initials fallback. */
  altText?: string;
};

export const AvatarUploaderField = ({ value, onChange, altText }: Props) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = React.useState<string | null>(null);
  const { setNotification } = useNotification();

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      setNotification({
        show: true,
        valid: false,
        title: 'Unsupported image type',
        message: 'Please upload a JPEG, PNG, or WEBP image.',
      });
      return;
    }

    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      setNotification({
        show: true,
        valid: false,
        title: 'Image is too large',
        message: 'Please upload an image under 5 MB.',
      });
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setCropSrc(dataUrl);
    } catch {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to read image',
        message: 'Please try again or use another image.',
      });
    }
  };

  const hasAvatar = !!value && value.length > 0;

  return (
    <div className="flex flex-col items-center gap-xl w-fit">
      <input
        type="file"
        accept={ALLOWED_AVATAR_TYPES.join(',')}
        hidden
        ref={inputRef}
        onChange={handleFileInputChange}
      />
      <Avatar
        size="2xl"
        className="size-40"
        name={altText}
        altText={altText}
        image={hasAvatar ? `data:image/jpeg;base64,${value}` : undefined}
        colorSeed={altText}
      />
      <div className="flex flex-col items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={() => inputRef.current?.click()}
        >
          {hasAvatar ? 'Change photo' : 'Upload photo'}
        </Button>
        {hasAvatar ? (
          <Button
            variant="link"
            size="sm"
            type="button"
            onClick={() => onChange(null)}
          >
            Remove photo
          </Button>
        ) : null}
      </div>
      {cropSrc ? (
        <AvatarCropDialog
          srcDataUrl={cropSrc}
          onCancel={() => setCropSrc(null)}
          onCropped={(base64) => {
            onChange(base64);
            setCropSrc(null);
          }}
        />
      ) : null}
    </div>
  );
};
